import React, { useState } from 'react';
import { Activity, AlertTriangle, CheckCircle, Clock, BarChart3 } from 'lucide-react';
import styles from './ReviewDashboard.module.css';
import bgPattern from '../../../../Assets/BRANDING-BG.png';
import { Header } from '../header/Header';
import { DashboardSidebar } from '../DashboardSidebar/DashboardSidebar';

const METRICS_DATA = [
    {
        id: 1,
        value: '99.95%',
        label: 'Service Availability',
        icon: Activity,
        indicator: 'green',
        change: null
    },
    {
        id: 2,
        value: '12',
        label: 'Open Incidents',
        icon: AlertTriangle,
        change: '-3 from last week',
        changeType: 'positive'
    },
    {
        id: 3,
        value: '47',
        label: 'Changes Completed',
        icon: CheckCircle,
        change: '+8 from last week',
        changeType: 'positive'
    },
    {
        id: 4,
        value: '2.4h',
        label: 'Avg Response Time',
        icon: Clock,
        target: 'Target <4h',
        change: null
    }
];

const SLA_DATA = [
    {
        id: 1,
        name: 'Availability SLA',
        value: 99.95,
        target: '99.9%',
        color: '#10b981'
    },
    {
        id: 2,
        name: 'P1 Response SLA',
        value: 100,
        target: '95%',
        color: '#10b981'
    },
    {
        id: 3,
        name: 'Change Success Rate',
        value: 94.2,
        target: '95%',
        color: '#eab308'
    }
];

const INCIDENT_DATA = [
    { priority: 'P1', label: 'Critical', count: 0, color: '#ef4444' },
    { priority: 'P2', label: 'High', count: 2, color: '#f97316' },
    { priority: 'P3', label: 'Medium', count: 5, color: '#eab308' },
    { priority: 'P4', label: 'Low', count: 5, color: '#9ca3af' }
];

const MetricCard = ({ metric }) => {
    const Icon = metric.icon;

    return (
        <div className={styles.metricCard}>
            <div className={styles.metricHeader}>
                <div className={styles.metricValue}>
                    {metric.value}
                    {metric.indicator && <span className={styles.greenDot} />}
                </div>
                <div className={styles.metricIcon}>
                    <Icon size={24} />
                </div>
            </div>
            <div className={styles.metricLabel}>{metric.label}</div>
            {metric.change && (
                <div className={`${styles.metricChange} ${styles[metric.changeType]}`}>
                    {metric.change}
                </div>
            )}
            {metric.target && (
                <div className={styles.metricTarget}>{metric.target}</div>
            )}
        </div>
    );
};

const SLABar = ({ sla }) => {
    return (
        <div className={styles.slaItem}>
            <div className={styles.slaHeader}>
                <span className={styles.slaName}>{sla.name}</span>
                <span className={styles.slaValue} style={{ color: sla.color }}>
                    {sla.value}%
                </span>
            </div>
            <div className={styles.slaBarContainer}>
                <div
                    className={styles.slaBar}
                    style={{
                        width: `${sla.value}%`,
                        backgroundColor: sla.color
                    }}
                />
            </div>
            <div className={styles.slaTarget}>Target {sla.target}</div>
        </div>
    );
};

export const ReviewDashboard = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const totalIncidents = INCIDENT_DATA.reduce((sum, item) => sum + item.count, 0);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.headerWrapper}>
                <Header onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
            </div>

            <div className={styles.mainWrapper}>
                <DashboardSidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />

                <div className={styles.container} style={{ backgroundImage: `url(${bgPattern})` }}>
                    <div className={styles.pageHeader}>
                        <div className={styles.headerLeft}>
                            <h1 className={styles.title}>Weekly AMS Review Dashboard</h1>
                            <p className={styles.subtitle}>
                                Stable metrics designed for governance meetings and screen sharing.
                            </p>
                        </div>
                        <div className={styles.headerRight}>
                            <span className={styles.lastUpdated}>
                                Last updated: <strong>Jan 15, 2026 at 09:00 AM</strong>
                            </span>
                        </div>
                    </div>

                    <div className={styles.metricsGrid}>
                        {METRICS_DATA.map((metric) => (
                            <MetricCard key={metric.id} metric={metric} />
                        ))}
                    </div>

                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryCard}>
                            <div className={styles.cardHeader}>
                                <BarChart3 size={20} className={styles.cardIcon} />
                                <h2 className={styles.cardTitle}>SLA Performance Summary</h2>
                            </div>
                            <div className={styles.slaList}>
                                {SLA_DATA.map((sla) => (
                                    <SLABar key={sla.id} sla={sla} />
                                ))}
                            </div>
                        </div>

                        <div className={styles.summaryCard}>
                            <div className={styles.cardHeader}>
                                <AlertTriangle size={20} className={styles.cardIconOrange} />
                                <h2 className={styles.cardTitle}>
                                    Incident Summary <span className={styles.cardSubtitle}>(Current Week)</span>
                                </h2>
                            </div>
                            <div className={styles.incidentList}>
                                {INCIDENT_DATA.map((incident) => (
                                    <div key={incident.priority} className={styles.incidentRow}>
                                        <span className={styles.incidentPriority}>{incident.priority}</span>
                                        <span
                                            className={styles.incidentLabel}
                                            style={{ color: incident.color }}
                                        >
                                            {incident.label}
                                        </span>
                                        <span className={styles.incidentCount}>{incident.count}</span>
                                    </div>
                                ))}
                                <div className={styles.incidentTotal}>
                                    <span className={styles.totalLabel}>Total Open</span>
                                    <span className={styles.totalCount}>{totalIncidents}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewDashboard;
